import { CameraProjection, } from '@skand/three-js-renderer';
import { Color, Vector2, Vector3 } from 'three';
import { calculateCentroid } from '../../Utils';
/**
 * Compare two lists of items
 */
function allEqual(a, b, eq) {
    if (a.length !== b.length)
        return false;
    for (let i = 0; i < a.length; i++) {
        if (!eq(a[i], b[i])) {
            return false;
        }
    }
    return true;
}
/**
 * Checks if a batchable mesh is compatible with a batch key
 */
function isInInstance(batchable, instanceKey) {
    return (allEqual(batchable.vertices, instanceKey.vertices, (a, b) => a.x === b.x && a.y === b.y && a.z === b.z) &&
        allEqual(batchable.indices, instanceKey.indices, (a, b) => a === b) &&
        batchable.material.type === instanceKey.material.type &&
        batchable.material.pointSize === instanceKey.material.pointSize &&
        batchable.material.faceCulling === instanceKey.material.faceCulling &&
        batchable.material.wireframe === instanceKey.material.wireframe &&
        batchable.material.vertexColors === instanceKey.material.vertexColors &&
        batchable.pixelSize === instanceKey.pixelSize &&
        batchable.renderLayer === instanceKey.renderLayer);
}
/**
 * Create a new instance key from a given batchable
 *
 * Only polygon meshes can be batched
 */
function createInstanceKey(batchable) {
    return {
        vertices: batchable.vertices.map(v => v.clone()),
        indices: [...batchable.indices],
        material: batchable.material,
        pixelSize: batchable.pixelSize,
        renderLayer: batchable.renderLayer,
    };
}
/**
 * Create the render object for an instance using a batchable as a template
 */
function createInstanceObject(renderer, batchable) {
    const mesh = renderer.createIndexedTriangleMesh();
    mesh.vertices = batchable.vertices.map(vertex => vertex.clone());
    mesh.indices = [...batchable.indices];
    mesh.resolve();
    const object = renderer.getObjectFactory().createRawInstancedMeshObject({
        mesh,
        positions: [],
        rotations: [],
        scales: [],
        position: { ecef: new Vector3() },
        scale: new Vector3(1, 1, 1),
        material: batchable.material,
    });
    object.setRenderLayer(batchable.renderLayer);
    return object;
}
/**
 * Compute the constant scale of a batchable.
 *
 * @param renderer
 * @param batchable
 */
function computeScale(renderer, batchable) {
    const camera = renderer.getWorld().getCamera();
    const resolution = renderer.getSize();
    if (batchable.pixelSize === undefined) {
        return batchable.scale.clone();
    }
    let scalingFactor = batchable.pixelSize / Math.max(resolution.x, resolution.y);
    if (camera.getProjectionMode() === CameraProjection.Perspective) {
        const cameraPosition = new Vector3();
        camera.getPositionECEF(cameraPosition);
        const cameraDistance = batchable.position.distanceTo(cameraPosition);
        scalingFactor *= cameraDistance * 3;
    }
    else {
        const orthographic = camera.getOrthographic();
        const dimensions = new Vector2(orthographic.getFrustumRight() - orthographic.getFrustumLeft(), orthographic.getFrustumTop() - orthographic.getFrustumBottom());
        const zoom = orthographic.getZoom();
        scalingFactor *= dimensions.length() / zoom;
    }
    return batchable.scale.clone().multiplyScalar(scalingFactor);
}
/**
 * Instanced mesh handler
 *
 * This is an alternative to MeshBatch, which groups models by mesh type.
 * MeshInstance groups by raw mesh data (i.e., vertex and index sets)
 */
export class MeshInstance {
    constructor(renderer) {
        Object.defineProperty(this, "renderer", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "instances", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "batchables", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.renderer = renderer;
        this.instances = new Map();
        this.batchables = new Map();
    }
    /**
     * Mark an instance as dirty so it is resolved in the next frame
     */
    refreshByKey(key) {
        const instance = this.instances.get(key);
        if (instance) {
            instance.dirty = true;
        }
    }
    /**
     * Signal the instance that an instance has been changed
     */
    refresh(batchable) {
        const key = this.batchables.get(batchable);
        if (key) {
            this.refreshByKey(key);
        }
    }
    /**
     * Add a mesh object to be instanced. If the mesh already exists, nothing will change.
     */
    addMesh(batchable) {
        if (this.batchables.has(batchable))
            return;
        // Find an instance to be grouped into
        let compatibleKey = null;
        for (const [key, _] of this.instances) {
            if (isInInstance(batchable, key)) {
                compatibleKey = key;
            }
        }
        // No compatible key exists, so we create a new instance group
        if (compatibleKey === null) {
            compatibleKey = createInstanceKey(batchable);
            const object = createInstanceObject(this.renderer, batchable);
            this.instances.set(compatibleKey, {
                object,
                batchables: [],
                dirty: false,
            });
            this.renderer.getWorld().addObject(object);
        }
        // Push the batchable onto the instance
        const instance = this.instances.get(compatibleKey);
        if (!instance)
            return;
        this.batchables.set(batchable, compatibleKey);
        instance.batchables.push(batchable);
        this.refreshByKey(compatibleKey);
    }
    /**
     * Remove an instanced mesh object
     */
    removeMesh(batchable) {
        const key = this.batchables.get(batchable);
        if (!key)
            return;
        const instance = this.instances.get(key);
        if (!instance)
            return;
        this.batchables.delete(batchable);
        const index = instance.batchables.findIndex(query => query === batchable);
        instance.batchables.splice(index, 1);
        this.refreshByKey(key);
    }
    /**
     * Get the instance world object containing the batchable.
     *
     * @param batchable
     * @returns
     */
    getInstanceObject(batchable) {
        const key = this.batchables.get(batchable);
        if (!key)
            return;
        const instance = this.instances.get(key);
        if (!instance)
            return;
        return instance.object;
    }
    /**
     * Get the picking id of a batchable.
     */
    getPickingId(batchable) {
        const key = this.batchables.get(batchable);
        if (!key)
            return;
        const instance = this.instances.get(key);
        if (!instance)
            return;
        const { batchables } = instance;
        const pickingId = batchables.findIndex(query => query === batchable);
        if (pickingId < 0)
            return;
        return pickingId;
    }
    /**
     * Main update loop for handling size attenuation and
     * reuploading instance data to the GPU, if needed
     */
    update() {
        for (const [_, instance] of this.instances) {
            const { dirty, object, batchables } = instance;
            if (!dirty)
                continue;
            const centroid = calculateCentroid(batchables.map(batchable => batchable.position));
            object.positions = batchables.map(batchable => batchable.position.clone().sub(centroid));
            object.rotations = batchables.map(batchable => batchable.rotation.clone());
            object.colors = batchables.map(batchable => {
                if (batchable.colors.length) {
                    return batchable.colors[0].clone();
                }
                return new Color(1, 1, 1);
            });
            object.scales = batchables.map(batchable => computeScale(this.renderer, batchable));
            object.setPositionECEF(centroid);
            object.resolve();
            // Reset dirty flag only if batchable has a defined pixel size (disabled size attenuation)
            if (batchables.length === 0 || batchables[0].pixelSize === undefined) {
                instance.dirty = false;
            }
        }
    }
}
